import {RootLayout} from '@raison/ui-kit';
import {EntityId} from '@reduxjs/toolkit';
import {type} from 'arktype';
import React, {useState} from 'react';
import {Navigate, useNavigate} from 'react-router';
import OperationHeader from '~/entities/operations-fiat/ui/OperationHeader';
import {executeOperationFinishNavigation} from '~/entities/transactions-history/model/TransactionDetailsPath';
import {useSearchParamStructured} from '~/library/navigation';
import {Decoder} from '~/library/prelude';
import {TransactionIdArkType} from '~/library/types';
import {BareAccount} from '~/processes/fiat-operations/model/Scheme/BareAccount';
import {ExchangeSettings} from '~/processes/fiat-operations/model/Scheme/ExchangeSettings';
import {renderAccountSelection} from '~/processes/fiat-operations/ui/Exchange/ExchangeFiat/renderAccountSelection';
import {useExchangeForm} from '~/processes/fiat-operations/ui/Exchange/ExchangeFiat/useExchangeForm';
import {OrderPlacedInfoPage} from '~/processes/fiat-operations/ui/Shared/OrderPlacedInfoPage';
import FiatOperationLayout from '../Shared/FiatOperationLayout';

export interface ExchangeFiatProps {
    readonly providerName?: string;
    readonly fromAccount: BareAccount;
    readonly toAccount: BareAccount;
    readonly settings: ExchangeSettings;
    readonly fromAccounts: BareAccount[];
    readonly toAccounts: BareAccount[];
}

const parseParam = type.never
    .or({
        step: 'undefined?',
    })
    .or({
        step: '"finish"',
        orderId: TransactionIdArkType,
    });
const decodeParam = Decoder.make(parseParam).orThrow();

function ExchangeFiat(props: ExchangeFiatProps) {
    const {fromAccount, toAccount, settings, fromAccounts, toAccounts, providerName} = props;

    const navigate = useNavigate();
    const [direction, setDirection] = useSearchParamStructured('exchange', {
        decode: decodeParam,
        fallback: {},
    });

    const [introCompleted, setIntroCompleted] = useState(false);

    const {
        amountField,
        amountInputOptions,
        onSubmit,
        isSubmitDisabled,
        renderHint,
        renderPreview,
        renderServerErrorModal,
        renderHintAction,
        receiveAmount,
    } = useExchangeForm({
        fromAccount,
        toAccount,
        settings,
        onSuccess(orderId) {
            setIntroCompleted(true);
            setDirection({
                step: 'finish',
                orderId,
            });
        },
    });

    if (!introCompleted && direction.step) {
        return <Navigate to={createOperationPath(fromAccount.id, toAccount.id)} replace />;
    }

    if (direction.step === 'finish') {
        return (
            <OrderPlacedInfoPage
                currency={{code: toAccount.currency, decimals: toAccount.decimals}}
                amount={receiveAmount}
                goToDetails={() => executeOperationFinishNavigation(direction.orderId, navigate)}
                direction={'in'}
                details={'Exchange request submitted.\nProcessing may take some time.'}
            />
        );
    }

    return (
        <RootLayout
            title='Exchange'
            pageHeader={<OperationHeader subtitle={providerName && `${providerName} account`} />}
        >
            <div className='flex flex-1 flex-col px-4 pb-4 pt-6'>
                <FiatOperationLayout
                    optionsAboveForm={renderAccountSelection({fromAccount, fromAccounts, toAccount, toAccounts})}
                    formAttributes={{onSubmit}}
                    isLoading={isSubmitDisabled}
                    inputOptions={{...amountInputOptions, ...amountField}}
                    inputHint={renderHint()}
                    hintAction={renderHintAction()}
                    submitPreviewEntries={renderPreview()}
                    submitActionTitle='Exchange'
                />
                {renderServerErrorModal()}
            </div>
        </RootLayout>
    );
}

const createOperationPath = (from: EntityId, to: EntityId) => {
    return `/operation/exchange/fiat/${from}/fiat/${to}`;
};

export default ExchangeFiat;
